import * as styles from 'common/components/GenericModal/styles';
import {
  ActionButtons,
  Container,
  Content,
  Image,
} from 'common/components/GenericModal/styles';
import {
  Box, Button, Divider,
} from '@mui/material';
import {
  CreateVideoTextData, UpdateVideoTextData, VideoText, VideoTextFormData,
} from 'containers/HomePageContainer/types/video-text';
import { DateTimePickerField } from 'common/components/DateTimePickerField';
import { Form, Formik } from 'formik';
import { Loader } from 'common/components/Loader';
import { LoadingButton } from '@mui/lab';
import { PageType } from 'common/enums/page-type.enum';
import { PositionSelect } from 'common/components/StaticWidgetSelect';
import { SourceSelect } from 'common/components/SourceSelect';
import { TextField } from 'common/components/TextField';
import { VideoTextConfiguration } from 'containers/HomePageContainer/components/PageConfigurator/VideoWithTextModalContent/VideoTextConfiguration';
import { mapVideoTextFormDataToRequest } from 'containers/HomePageContainer/components/PageConfigurator/VideoWithTextModalContent/mappers/map-video-text-form-data-to-request';
import { mapWidgetDataToFormInitialValues } from 'containers/HomePageContainer/components/PageConfigurator/VideoWithTextModalContent/mappers/map-widget-data-to-form-initial-values';
import { useCreateWidgetMutation } from 'containers/HomePageContainer/store/server/use-create-widget-mutation';
import {
  useDefaultInitialValues,
} from 'containers/HomePageContainer/components/PageConfigurator/VideoWithTextModalContent/hooks/use-default-initial-values';
import { useSearch } from 'containers/HomePageContainer/hooks/use-search';
import { useUpdateWidgetMutation } from 'containers/HomePageContainer/store/server/use-update-widget-mutation';
import { useWidget } from 'containers/HomePageContainer/store/server/use-widget';
import { videoTextWidgetSchema } from 'containers/HomePageContainer/components/PageConfigurator/VideoWithTextModalContent/video-text-widget.schema';
import CancelIcon from '@mui/icons-material/Cancel';
import React, { FC, useCallback } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import VideoTextImage from 'assets/widgets/thumbnails/Video_Text_Widget.png';

export interface VideoTextModalContentProps {
  closeModal: () => void;
  id?: string;
  pageType: PageType;
}

export const VideoTextModalContent: FC<VideoTextModalContentProps> = ({
  closeModal,
  id,
  pageType,
}) => {
  const defaultInitialValues = useDefaultInitialValues();
  const { culture } = useSearch();
  const { data, isLoading } = useWidget<VideoText>(id);
  const createMutation = useCreateWidgetMutation(closeModal);
  const updateMutation = useUpdateWidgetMutation(closeModal);

  const handleSubmit = useCallback((values: VideoTextFormData) => {
    if (id) {
      updateMutation.mutate({
        id,
        data: mapVideoTextFormDataToRequest(values, pageType) as UpdateVideoTextData,
      });
    } else {
      createMutation.mutate(
        mapVideoTextFormDataToRequest(
          values,
          pageType,
          culture,
        ) as CreateVideoTextData,
      );
    }
  }, [id, culture, createMutation, updateMutation, pageType]);

  if (pageType === PageType.Plp) {
    defaultInitialValues.position = 1;
  }
  if (id && isLoading) {
    return <Container><Loader isLoading /></Container>;
  }

  return (
    <Formik
      initialValues={data ? mapWidgetDataToFormInitialValues(data) : defaultInitialValues}
      validationSchema={videoTextWidgetSchema}
      onSubmit={handleSubmit}
    >
      {({ dirty }) => (
        <Form>
          <Container>
            <Content>
              <Box display="flex">
                <Image src={VideoTextImage} alt="banner widget" />
                <Box pl={3.75}>
                  <TextField name="name" label="Widget Name" fullWidth />
                  <Box display="flex">
                    <DateTimePickerField name="activeFrom" label="Valid from" sx={styles.dateTimePicker} />
                    <DateTimePickerField name="activeTo" label="Valid to" sx={styles.dateTimePicker} />
                    {pageType !== PageType.Plp && (
                      <SourceSelect name="sources" />
                    )}
                    {pageType === PageType.Plp && (
                      <PositionSelect name="position" />
                    )}
                  </Box>
                </Box>
              </Box>
              <Divider sx={styles.divider} />
              <VideoTextConfiguration />
            </Content>
            <ActionButtons>
              <Button
                onClick={closeModal}
                variant="outlined"
                size="large"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={createMutation.isLoading || updateMutation.isLoading}
                disabled={data && !dirty}
                variant="contained"
                type="submit"
                size="large"
                startIcon={<SaveIcon />}
              >
                Save
              </LoadingButton>
            </ActionButtons>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

VideoTextModalContent.defaultProps = {
  id: '',
};
